import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stage, ChapterTag } from "@/components/Reveal";
import { Thread } from "@/components/Motifs";

const PLANES = [
  { img: "/assets/img3296.webp", alt: "How are you arriving today — Threshold check in", x: "3%", y: "14%", mx: "4%", my: "9%", w: 260, ar: 0.487, depth: 0.8, win: [0.04, 0.36] },
  { img: "/assets/img3297.webp", alt: "How would you like to enter — Threshold direction", x: "37%", y: "30%", mx: "29%", my: "26%", w: 260, ar: 0.527, depth: 1.4, win: [0.36, 0.62], sel: true },
  { img: "/assets/img3298.jpg", alt: "What kind of conversation do you want right now — Threshold energy", x: "70%", y: "12%", mx: "55%", my: "5%", w: 290, ar: 0.812, depth: 2.0, win: [0.6, 0.86] },
];

const INTENTION_RANGES = {
  arrival: [0.06, 0.13, 0.28, 0.36],
  entry: [0.38, 0.45, 0.54, 0.62],
  conversation: [0.64, 0.7, 0.78, 0.85],
  statement: [0.88, 0.95],
};

const Plane = ({ pl, i, progress, isMobile }) => {
  const mid = (pl.win[0] + pl.win[1]) / 2;
  const y = useTransform(progress, [0, 1], [90 * pl.depth, -110 * pl.depth]);
  const z = useTransform(progress, [Math.max(0, pl.win[0] - 0.18), mid, Math.min(1, pl.win[1] + 0.1)], [-420, 0, 380]);
  const dim = useTransform(progress, [pl.win[0], pl.win[0] + 0.07, pl.win[1] - 0.06, pl.win[1]], [0.3, 1, 1, 0.3]);
  const scale = useTransform(progress, [pl.win[0], pl.win[0] + 0.07], [0.97, 1]);
  const passOp = useTransform(progress, [pl.win[1], Math.min(1, pl.win[1] + 0.07)], [1, 0]);
  const selOp = useTransform(progress, [0.4, 0.47, 0.6, 0.66], [0, 1, 1, 0]);
  const stub = useTransform(progress, [0.46, 0.58], [0, 1]);
  return (
    <motion.figure
      className="th-plane"
      style={{ left: isMobile ? pl.mx : pl.x, top: isMobile ? pl.my : pl.y, y, z, opacity: passOp }}
      data-testid={`intention-plane-${i}`}
    >
      <motion.div
        className="th-plane-frame"
        style={{ opacity: dim, scale, width: `min(${pl.w}px, ${isMobile ? 38 : 42}vw)`, aspectRatio: pl.ar }}
      >
        <img src={pl.img} alt={pl.alt} loading="lazy" decoding="async" />
        {pl.sel && (
          <>
            <motion.div className="th-selcard" style={{ opacity: selOp }} data-testid="intention-selection" />
            <svg className="th-stub" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <motion.path
                d="M 52 74 C 52 84, 50 92, 50 102"
                stroke="var(--amber)"
                strokeWidth={1.4}
                vectorEffect="non-scaling-stroke"
                fill="none"
                strokeLinecap="round"
                style={{ pathLength: stub }}
              />
            </svg>
          </>
        )}
      </motion.div>
    </motion.figure>
  );
};

export default function Intention() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const thread = useTransform(p, [0.52, 0.88], [0, 1]);
  const ghostX = useTransform(p, [0, 1], [70, -70]);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <section id="scene-intention" ref={ref} className="th-scene" style={{ height: "300vh" }} data-testid="scene-intention">
      <div className="th-sticky">
        <ChapterTag index="03" name="Arrive with Intention" />
        <motion.div className="th-ghostword" style={{ x: ghostX, top: "6%", left: "6%", fontSize: "17vw" }} aria-hidden="true">
          intention
        </motion.div>
        <div className="th-rooms">
          {PLANES.map((pl, i) => (
            <Plane key={pl.img} pl={pl} i={i} progress={p} isMobile={isMobile} />
          ))}
        </div>
        <div className="th-fog" aria-hidden="true" />
        <Thread
          d="M 437 905 C 500 820, 570 750, 650 680 S 870 520, 1030 500"
          viewBox="0 0 1000 1000"
          className="absolute inset-0 w-full h-full"
          progress={thread}
          stroke="var(--sage-deep)"
          sw={1.3}
        />
        <Stage progress={p} range={INTENTION_RANGES.arrival}>
          <h3 className="th-question th-halo">How are you arriving today?</h3>
        </Stage>
        <Stage progress={p} range={INTENTION_RANGES.entry}>
          <h3 className="th-question th-halo">How would you like to enter?</h3>
        </Stage>
        <Stage progress={p} range={INTENTION_RANGES.conversation}>
          <h3 className="th-question th-halo">
            What kind of conversation
            <br />
            do you want right now?
          </h3>
        </Stage>
        <Stage progress={p} range={INTENTION_RANGES.statement} hold>
          <h3 className="th-statement th-halo" data-testid="intention-statement">
            Start with intention,
            <br />
            not impression.
          </h3>
        </Stage>
      </div>
    </section>
  );
}
